/**
 * Mental Health Translations Module (English)
 * 
 * Contains: Mental Health Index, Crisis Statistics, RISE Projects, Regional Analysis
 * 
 * @module mentalHealth
 * @language en
 */

export const mentalHealth = {
  mentalHealthOld: {
    title: "Mental Health Index",
    subtitle: "Comprehensive mental health and wellbeing analysis",
    globalAverage: "Global Average",
    categoryDistribution: "Category Distribution",
    assessedDimensions: "Assessed Dimensions",
    methodology: "Methodology",
    top5Countries: "Top 5 Countries",
    regionalLeaders: "Regional Leaders",
    leadersByDimension: "Leaders by Dimension"
  },
  mentalHealthIndex: {
    title: "Mental Health Index",
    subtitle: "Comprehensive mental health and wellbeing analysis",
    globalAverage: "Global Average",
    points: "points",
    criticalRisk: "critical risk",
    categoryDistribution: "Category Distribution",
    assessedDimensions: "Assessed Dimensions",
    methodology: "Methodology",
    top5Countries: "Top 5 Countries",
    regionalLeaders: "Regional Leaders",
    leadersByDimension: "Leaders by Dimension",
    overall: "Overall",
    youthSuicide: "Youth Suicide",
    serviceAccess: "Service Access",
    prevalence: "Prevalence",
    globalMHIScore: "Global MHI Score",
    coverageNote: "Based on 114 JCI countries",
    lowestRisk: "lowest risk",
    highestRisk: "highest risk",
    mediumRisk: "medium risk",
    regionalRankings: "Regional Index",
    rank: "Rank",
    region: "Region",
    score: "Score",
    performance: "Performance",
    excellent: "Excellent",
    good: "Good",
    moderate: "Moderate",
    concerning: "Concerning",
    critical: "Critical"
  },
  mentalHealthPage: {
    pageTitle: "Mental Health Dimension",
    weightInFII: "weight in YFI",
    measuringWellbeing: "Measuring wellbeing, resilience, and access to mental health support worldwide.",
    quote: "There is no health without mental health.",
    fiiWeightDistribution: "YFI weight distribution",
    mentalHealth: "Mental Health",
    workforce: "Workforce",
    entrepreneurship: "Entrepreneurship",
    understandCrisis: "Understand the crisis"
  },
  mentalHealth: {
    crisisTitle: "The Mental Health Crisis in Numbers",
    crisisSubtitle: "Critical statistics affecting global youth mental wellness",
    crisis: {
      inSeven: "1 in 7",
      inSevenSuffix: "in 7",
      inThree: "1 in 3",
      inThreeSuffix: "in 3",
      th: "th",
      stat1Label: "youth globally experience mental health disorders",
      stat2Label: "depression prevalence rate (doubled from 8.5% pre-pandemic)",
      stat3Label: "anxiety prevalence rate (doubled from 11% pre-pandemic)",
      stat4Label: "youth cite job insecurity as primary anxiety source",
      stat5Label: "young Africans report extreme anxiety about job prospects",
      stat6Label: "feel worse off than parents in North America, Europe, MENA",
      stat7Label: "global average access to mental health services (range: 10-90%)",
      stat8Label: "cause of death among 15-19 year olds is suicide",
      stat9Label: "of people with mental illness do not receive treatment"
    },
    severity: {
      crisis: "Crisis",
      severeGap: "Severe Gap",
      concerning: "Concerning"
    },
    fiiDimensionTitle: "YFI Mental Health Dimension",
    fiiDimensionSubtitle: "Mental Health Score = 40% of Future Investment Index",
    regions: {
      europe: "Europe",
      americas: "America",
      asiaPacific: "Asia and the Pacific",
      africaMe: "Africa/Middle East"
    },
    status: {
      good: "Good",
      moderate: "Moderate",
      atRisk: "At Risk"
    },
    fii: {
      serviceAccess: "Service Access",
      serviceAccessDef: "Percentage of 18-40 year olds with access to mental health services (primary care, specialists, crisis services)",
      countriesAssessed: "144 countries assessed",
      serviceAccessHighest: "Luxembourg, Denmark, Iceland (80-90%)",
      serviceAccessLowest: "Somalia, Yemen, Chad (<5%)",
      europeNote: "Strongest infrastructure",
      americasNote: "Varies by country",
      asiaPacificNote: "Highest variation",
      africaMeNote: "Critical gaps",
      serviceAccessFinding: "Access ≠ quality. Provider shortage: 13 globally per 100k vs 300+ in high-income countries (40x disparity)",
      prevalence: "Disorder Prevalence",
      prevalenceDef: "Percentage of youth experiencing depression and/or anxiety (clinically significant symptoms)",
      trend1: "Post-COVID spike: Doubled from 2019 baseline",
      trend2: "Gender gap: Women 1.5x-2x higher rates",
      trend3: "Age concentration: Highest in 20-24 group",
      trend4: "Rising trend: Anxiety +52% increase (1990-2021)",
      prevalenceFinding: "Prevalence is relatively uniform globally, but access is not",
      suicideMortality: "Suicide Mortality",
      suicideDef: "Age-standardized suicide rate per 100,000 youth (18-40 years)",
      suicideHighest: "Lithuania (25-30 per 100k), Russia (20-25)",
      suicideMiddle: "North America (13-15), European average (10-12)",
      suicideLower: "Asian countries (5-12, varies widely)",
      suicideLowest: "African countries (1.5-5 per 100k, likely underreported)",
      suicideCriticalFinding: "Suicide = 4th leading cause of death for ages 15-29 (WHO)",
      suicideNote: "Low-income countries likely underreport due to healthcare access, stigma, certification gaps",
      subWeight: "Sub-weight",
      showDetails: "Show details",
      globalAverage: "Global average",
      highest: "Highest",
      lowest: "Lowest",
      middle: "Middle",
      lower: "Lower",
      regionalBreakdown: "Regional Breakdown",
      currentPrevalence: "Current prevalence (2021-2025)",
      depression: "Depression",
      anxiety: "Anxiety",
      both: "Both",
      keyTrends: "Key Trends",
      globalRange: "Global range",
      keyFinding: "Key finding",
      criticalFinding: "Critical finding"
    },
    regionalScoresTitle: "Regional Mental Health Scores",
    regionalScoresSubtitle: "YFI Mental Health Dimension: Regional Performance",
    regional: {
      serviceAccess: "Service Access",
      prevalence: "Prevalence",
      suicideRate: "Suicide Rate",
      drivers: "Drivers",
      opportunity: "Opportunity",
      note: "Note",
      europeDrivers: "Universal healthcare systems, anti-stigma programs, workplace integration",
      europeOpportunity: "JCI peer programs can accelerate access pathways",
      europeNote: "European success is policy-driven — models can be adapted globally",
      asiaPacificDrivers: "Economic variation: Korea & Japan high, South Asia low",
      asiaPacificOpportunity: "Digital mental health solutions (mobile-first)",
      asiaPacificNote: "Cultural stigma remains high — youth-focused approaches are key",
      americasDrivers: "North America strong, Latin America has major gaps",
      americasOpportunity: "Mental health-focused RISE can be scaled here",
      americasNote: "High US suicide rates despite good access = treatment ≠ outcomes",
      africaMeDrivers: "Infrastructure shortage, not prevalence (disorder rates are similar globally)",
      africaMeOpportunity: "Community-based models (JCI can fit) that bypass clinical settings",
      africaMeNote: "Highest potential for JCI partnerships — peer support model is scalable",
      americasGap: "30-40% service coverage gap between US/Canada and Latin America",
      africaMeGap: "Critical gap: only 10-30% service coverage vs 70%+ in Europe",
      gapLabel: "Gap"
    },
    gapAnalysis: {
      title: "YFI Regional Gap Analysis",
      description: "The gap between Europe and Africa and the Middle East represents the primary inequality in global youth opportunity",
      pointGap: "point gap",
      comparison: "(Europe 75.28 vs Africa/ME 53.12)"
    },
    legend: {
      good: "Good (70+)",
      moderate: "Moderate (60-69)",
      atRisk: "At Risk (<60)"
    },
    riseTitle: "JCI RISE Mental Health Initiatives",
    riseSubtitle: "JCI mental health initiatives aligned with YFI Mental Health gap",
    rise: {
      title: "JCI's Response: RISE Mental Health Projects",
      subtitle: "Scale of JCI Mental Health Response",
      mentalHealthProjects: "Mental Health Projects",
      ofAllRiseInitiatives: "of all JCI RISE initiatives",
      budgetMobilized: "Budget Mobilized",
      millions: "millions",
      youthTouched: "Youth Touched",
      programTypes: "Program Types",
      impact: "Impact",
      examples: "Examples",
      awarenessName: "Mental Health Awareness Campaigns",
      awarenessDesc: "Community education events, social media campaigns, school partnerships",
      awarenessImpact: "50,000+ reached directly and indirectly",
      awarenessEx1: "'Mental Health Matters' events",
      awarenessEx2: "2M+ social media impressions",
      awarenessEx3: "School peer support training",
      peerSupportName: "Peer Support Networks",
      peerSupportDesc: "Peer mentor training, support groups, crisis hotline volunteers",
      peerSupportImpact: "5,000+ peer supporters trained",
      peerSupportEx1: "Crisis intervention training",
      peerSupportEx2: "Depression/anxiety support groups",
      peerSupportEx3: "Online + in-person circles",
      crisisTrainingName: "Crisis Training & Response",
      crisisTrainingDesc: "First responder training, suicide prevention, emergency protocols",
      crisisTrainingImpact: "1,200+ crisis responders trained",
      crisisTrainingEx1: "Suicide prevention workshops",
      crisisTrainingEx2: "Mental health first aid",
      crisisTrainingEx3: "Emergency hotline staffing",
      workplaceWellnessName: "Workplace Wellness Programs",
      workplaceWellnessDesc: "Corporate partnerships, workplace mental health training, EAP integration",
      workplaceWellnessImpact: "200+ companies reached",
      workplaceWellnessEx1: "Employee wellness workshops",
      workplaceWellnessEx2: "Manager mental health training",
      workplaceWellnessEx3: "Stress reduction programs",
      regionalDistribution: "Regional Distribution",
      projects: "projects",
      distributionNote: "Asia and the Pacific leads in mental health projects due to large member base. Africa and the Middle East shows highest growth rate.",
      outcomesMeasured: "Outcomes Measured",
      outcome1: "Increased mental health awareness",
      outcome2: "Reduced stigma around seeking help",
      outcome3: "Connected to professional resources",
      outcome4: "Reported improved wellbeing",
      crisisInterventions: "crisis interventions conducted"
    },
    actions: {
      title: "Recommended Actions for JCI",
      subtitle: "Strategic priorities to close the mental health gap",
      effort: "Effort",
      impactLabel: "Impact",
      action1Title: "Scale RISE Mental Health Projects",
      action1Desc: "Expand mental health RISE projects from 22% to 40% of all initiatives, prioritizing Africa/ME regions",
      action1Effort: "12-18 months",
      action1Impact: "2x beneficiary reach",
      action2Title: "Train 10,000 Peer Supporters",
      action2Desc: "Develop certified peer support training curriculum and deploy across all regions by 2030",
      action2Effort: "24-36 months",
      action2Impact: "Sustainable model",
      action3Title: "Workplace Wellness Expansion",
      action3Desc: "Partner with corporate sponsors to bring mental health training to young professionals globally",
      action3Effort: "6-12 months",
      action3Impact: "Corporate partnerships",
      action4Title: "Digital Mental Health Platform",
      action4Desc: "Develop mobile-first mental health resources accessible across all JCI regions",
      action4Effort: "18-24 months",
      action4Impact: "Global accessibility",
      action5Title: "Community Mental Health Ambassador Program",
      action5Desc: "Train local mental health ambassadors in each JCI local organization to provide ongoing community support",
      action5Effort: "12-24 months",
      action5Impact: "Grassroots scaling"
    },
    dataSources: "Data Sources",
    callToAction: {
      title: "Mental Health is the Foundation",
      description: "The data is clear: mental health is the primary differentiator between high and low opportunity regions. JCI's peer support model is uniquely positioned to address this gap at scale.",
      point1: "Mental health represents 40% of the YFI score — the largest single factor",
      point2: "22-point gap between Europe and Africa/ME is primarily driven by mental health infrastructure",
      point3: "Peer support models (non-clinical) are ideal for emerging markets — scalable and culturally appropriate",
      actionTitle: "Recommended Actions",
      action1: "Scale mental health RISE projects in Africa/ME (current: 22% of projects)",
      action2: "Train 10,000+ peer mental health supporters by 2030",
      action3: "Integrate mental health component into all JCI programs",
      action4: "Partner with WHO and mental health NGOs for curriculum development"
    },
    regionalAnalysis: {
      title: "YFI Regional Analysis: Mental Health Deep Dive",
      subtitle: "Detailed breakdown of mental health indicators by JCI region",
      europe: {
        strength1: "Highest overall MH score (78/100)",
        strength2: "Universal mental health coverage in Nordic countries",
        strength3: "Workplace mental health programs widespread",
        strength4: "Low stigma in Western Europe",
        challenge1: "Southern/Eastern Europe lag behind (60-70 scores)",
        challenge2: "Youth isolation increasing post-pandemic",
        challenge3: "Long wait times for specialized care",
        challenge4: "Rural access gaps in some countries",
        recommendation1: "Leverage high-performing local organizations as models",
        recommendation2: "Focus on youth isolation and loneliness",
        recommendation3: "Scale workplace mental health programs",
        insight: "Europe's success is policy-driven — models can be adapted globally",
        expansion: "Focus on Southern/Eastern European local organizations"
      },
      asiaPacific: {
        strength1: "Rapid digital mental health adoption",
        strength2: "Strong community/family support systems",
        strength3: "Government investment increasing (Japan, Korea, Australia)",
        strength4: "Young population receptive to new approaches",
        challenge1: "High stigma in many cultures (reduces help-seeking)",
        challenge2: "Extreme academic/work pressure (especially East Asia)",
        challenge3: "Wide variation: Australia (75) vs South Asia (45-55)",
        challenge4: "Limited trained professionals outside major cities",
        recommendation1: "Digital-first mental health solutions",
        recommendation2: "Youth-focused anti-stigma campaigns",
        recommendation3: "Peer support networks in schools/workplaces",
        insight: "Cultural stigma is the barrier — youth-focused approaches are key",
        expansion: "Scale digital mental health across the region"
      },
      americas: {
        limitedData: "Limited complete data across Latin America",
        strength1: "North America: Strong access (70-80% coverage)",
        strength2: "Growing mental health awareness movement",
        strength3: "Telehealth expansion post-COVID",
        strength4: "Active mental health advocacy organizations",
        challenge1: "Latin America: Major access gaps (30-50% coverage)",
        challenge2: "High US suicide rates despite good access",
        challenge3: "Cost barriers in US healthcare system",
        challenge4: "Rural access challenges across the region",
        recommendation1: "Focus on Latin America mental health (widest gap)",
        recommendation2: "Scale JIB + CYE programs in lower-scoring nations",
        recommendation3: "Develop women entrepreneur support programs",
        insight: "Only 8% of global JCI but 15% of world = GROWTH POTENTIAL",
        expansion: "Scaling from 90 RISE projects → 150+"
      },
      africaMe: {
        limitedData: "Limited complete data - many estimated",
        strength1: "Youth population: Youngest region (avg 29 years)",
        strength2: "Entrepreneurship aspiration: 52% of youth want to start businesses",
        strength3: "Growth potential: 10-20% GDP growth annually (some regions)",
        strength4: "Digital leapfrogging: Mobile-first adoption",
        challenge1: "Mental health access: Only 10-30% coverage (EU is 70%)",
        challenge2: "Healthcare infrastructure: Critical shortage (1-2 per 100k)",
        challenge3: "Youth unemployment: 14-16% (global is 13%)",
        challenge4: "NEET crisis: 1 in 3 youth not in school/work",
        challenge5: "Funding gap: $1.7T globally, 30% Africa/ME",
        challenge6: "Gender inequality: Women face 2x barriers across all pillars",
        recommendation1: "Mental health: Peer support model (non-clinical)",
        recommendation2: "Skills training: STEM + digital (via JCI)",
        recommendation3: "Youth entrepreneurship: Microfinance + mentorship",
        recommendation4: "Gender empowerment: Double focus on women",
        insight: "YFI Gap: 22 points vs Europe = addressable problem. RISE projects: 220 only (22%), need to 2-3x scale",
        expansion: "CRITICAL: JCI expansion here = highest global impact. 3-4 year plan to close 50% of YFI gap"
      }
    }
  },
  globalTrends: {
    title: "Global Mental Health Trends",
    subtitle: "Mental health prevalence has increased by over 25% since 2020",
    stat1Label: "Increase in youth mental health prevalence",
    stat1Period: "since 2019",
    stat2Label: "Increase in youth suicide rates",
    stat2Period: "since 2010",
    stat3Label: "Increase in youth mental health service usage",
    stat3Period: "since 2018",
    globalTrendLabel: "Global Trend: Mental Health Awareness",
    regionalLabel: "Regional Breakdown",
    trendDescription: "The COVID-19 pandemic exacerbated mental health challenges, disproportionately affecting youth. However, awareness is rising and stigma around seeking help is decreasing."
  },
  futureOriented: {
    title: "Future-Oriented Insights",
    subtitle: "Mental health projections and opportunities across JCI regions",
    trend1: {
      title: "Growing Prevalence Globally",
      description: "Depression and anxiety projected to increase another 15% by 2030, making prevention programs and early intervention a priority."
    },
    trend2: {
      title: "Digital Mental Health Growth",
      description: "Teletherapy and mental health apps projected to grow 100% by 2030, expanding access to underserved regions."
    },
    trend3: {
      title: "Workplace Mental Health Focus",
      description: "Employers increasing investment in employee wellbeing, projected $1 trillion annually in wellness programs by 2030."
    },
    trend4: {
      title: "Youth-Led Peer Support",
      description: "Peer support models proving scalable and culturally relevant as alternatives to clinical resources."
    }
  }
} as const;

